import { isHomepage } from './pageContext.js';

const ANIM_DURATION = 900;
// Wenn true, scrollt die Seite während der Animation um die Höhendifferenz
// zwischen Start- und End-Header mit. Auf false setzen, um nur die
// Header-Animation zu fahren.
const HEADER_SCROLL_SYNC = true;
const LOGO_W = 200;   // End-State Logo-Breite (px)
const LOGO_H = 34;    // End-State Logo-Höhe (px)
const LOGO_START_RATIO = 0.5; // Start-Logo-Breite relativ zur Innenbreite des Header-Containers
const GRID_ROW_H = 42; // Höhe der Grid-Zeile (getrieben durch Nav)
const GRID_ROW_CENTER = GRID_ROW_H / 2; // 21px – Mitte aller Grid-Items

function getContainerInnerWidth(header) {
  const container = header.querySelector('.container');
  if (!container) {
    return Math.min(window.innerWidth - 40, 1920);
  }
  const style = getComputedStyle(container);
  return container.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
}

function computeHeaderVars(header) {
  const scaleMax = (getContainerInnerWidth(header) * LOGO_START_RATIO) / LOGO_W;

  const paddingMax = (LOGO_H / 2) * scaleMax - GRID_ROW_CENTER;
  const heightMax = LOGO_H * scaleMax + 20;
  const navTYMax = (heightMax - GRID_ROW_CENTER) - (paddingMax + GRID_ROW_CENTER);

  header.style.setProperty('--header-logo-scale-max', scaleMax);
  header.style.setProperty('--header-padding-max', paddingMax + 'px');
  header.style.setProperty('--header-height-max', heightMax + 'px');
  header.style.setProperty('--header-nav-ty-max', navTYMax + 'px');
}

// Setzt --header-offset auf die echte Header-Höhe, damit der Content auf
// Unterseiten (CSS: body:not(.home) .mainContent) nicht unter dem fixierten
// Header verschwindet. Nur auf Unterseiten relevant – auf der Startseite
// animiert der Header seine Höhe selbst und der Hero liegt dahinter.
//
// WICHTIG: Gemessen wird die .headerWrapper, NICHT #header. #header ist im
// Fluss leer (sein einziges Kind .headerWrapper ist position:fixed), hätte also
// offsetHeight 0 – der Offset würde fälschlich auf 0px gesetzt und der Content
// (inkl. Breadcrumb) läge hinter dem Header.
function applyHeaderOffset(header) {
  const wrapper = header.querySelector('.headerWrapper') || header;
  document.documentElement.style.setProperty('--header-offset', wrapper.offsetHeight + 'px');
}

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function measureHeaderShrinkPx(header) {
  const startH = header.offsetHeight;
  header.style.setProperty('--header-scroll-progress', '1');
  const endH = header.offsetHeight;
  header.style.setProperty('--header-scroll-progress', '0');
  return Math.max(0, startH - endH);
}

function runHeaderAnimation(header) {
  const shrinkPx = HEADER_SCROLL_SYNC ? measureHeaderShrinkPx(header) : 0;
  const baseline = window.scrollY;
  const start = performance.now();

  function tick(now) {
    const t = Math.min(1, (now - start) / ANIM_DURATION);
    const eased = easeInOutCubic(t);
    header.style.setProperty('--header-scroll-progress', eased);

    if (shrinkPx > 0) {
      window.scrollTo(0, baseline + eased * shrinkPx);
    }

    if (t < 1) requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
}

export function initHeader() {
  const header = document.getElementById('header');
  if (!header) return;

  computeHeaderVars(header);

  // Nicht body.home abfragen: Kollektions-Detailseiten erben die Klasse von
  // der Sprach-Startseite (siehe pageContext.js).
  const isHome = isHomepage();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Unterseiten: echten Header-Offset für den Content-Abstand setzen
  if (!isHome) {
    applyHeaderOffset(header);

    // Header-Höhe ändert sich noch nach dem ersten Messen (Logo-Bild lädt
    // asynchron, Webfont, Umbruch der Navigation). ResizeObserver hält den
    // Offset automatisch synchron, damit die Breadcrumb nie hinter dem Header
    // liegt.
    const wrapper = header.querySelector('.headerWrapper');
    if (wrapper && 'ResizeObserver' in window) {
      new ResizeObserver(() => applyHeaderOffset(header)).observe(wrapper);
    }
  }

  // Nur auf der Startseite Animation aufbauen; CSS/PHP steuern den Rest
  if (isHome && !reducedMotion) {
    const SCROLL_DOWN_KEYS = new Set([' ', 'Spacebar', 'ArrowDown', 'PageDown', 'End']);
    let triggered = false;
    let touchStartY = 0;

    const onWheel = (e) => { if (e.deltaY > 0) trigger(); };
    const onTouchStart = (e) => { touchStartY = e.touches[0].clientY; };
    const onTouchMove = (e) => { if (e.touches[0].clientY < touchStartY) trigger(); };
    const onKeyDown = (e) => { if (SCROLL_DOWN_KEYS.has(e.key)) trigger(); };

    const trigger = () => {
      if (triggered) return;
      triggered = true;
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('keydown', onKeyDown);
      // Start-Signal für abhängige Module (z. B. sticky Kontaktbox)
      window.dispatchEvent(new CustomEvent('header:animationstart'));
      runHeaderAnimation(header);
    };

    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('keydown', onKeyDown);
  }

  window.addEventListener('resize', () => {
    computeHeaderVars(header);
    if (!isHome) {
      applyHeaderOffset(header);
    }
  }, { passive: true });
}